/** Envelope de sucesso da API (CLAUDE.md 7). */
export interface ApiEnvelope<T> {
  data: T;
  meta?: ApiMeta;
}

export interface ApiMeta {
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

/** Envelope de erro da API. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]> | Record<string, unknown>;
  };
}

/** Codigos de erro que o frontend trata de forma especifica. */
export const API_ERROR_CODES = {
  invalidCredentials: "invalid_credentials",
  unauthenticated: "unauthenticated",
  forbidden: "forbidden",
  notFound: "not_found",
  invalidCsrf: "invalid_csrf_token",
  tenantRequired: "tenant_required",
  tenantSuspended: "tenant_suspended",
  authUnavailable: "auth_unavailable",
  rateLimited: "rate_limited",
  validationFailed: "validation_failed",
  network: "network_error",
} as const;

export type ApiErrorCode = (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES] | string;

export type UserRole = "therapist" | "guardian";

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: "invited" | "active" | "suspended";
  avatar_url: string | null;
  /** So preenchido para guardian; null para therapist (RF-02.6). */
  consent_given: boolean | null;
  /** So preenchido para therapist (RF-06.15). */
  signature: string | null;
  notification_prefs: NotificationPrefs;
  created_at: string | null;
}

export interface SessionOperator {
  id: string;
  name: string;
  email: string;
  status: string;
  role: "operator";
}

export interface SessionTenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  timezone: string;
}

/** Resposta de login, refresh e /me. Nunca contem token: eles vivem so em
 *  cookie httpOnly (CLAUDE.md 6). */
export interface Session {
  user?: SessionUser;
  operator?: SessionOperator;
  tenant?: SessionTenant;
  expires_at?: string;
}

// --- Fase 2 -----------------------------------------------------------

export type AttachmentKind = "photo" | "video" | "audio";

export interface Attachment {
  id: string;
  kind: AttachmentKind;
  content_type: string;
  byte_size: number;
  url: string;
  created_at: string | null;
}

export interface OrientationItem {
  id: string;
  title: string;
  position: number;
  checked_today: boolean;
  adherence?: { yes_count: number; expected_days: number; percent: number | null };
}

export type OrientationSource = "template" | "custom" | "ai_draft";

export interface Orientation {
  id: string;
  patient_id: string;
  title: string;
  body: string;
  week_start: string;
  week_end: string;
  source: OrientationSource;
  active: boolean;
  created_by: { id: string; name: string };
  items: OrientationItem[];
  created_at: string | null;
}

export interface OrientationTemplate {
  id: string;
  title: string;
  body: string;
  items: string[];
  created_at: string | null;
}

export interface Message {
  id: string;
  patient_id: string;
  sender: { id: string; name: string; role: UserRole };
  body: string;
  read_at: string | null;
  created_at: string | null;
}

export type AlertKind = "crises_increase" | "mood_drop" | "low_adherence" | "missing_records";
export type AlertSeverity = "warning" | "critical";

export interface Alert {
  id: string;
  patient: { id: string; name: string };
  kind: AlertKind;
  severity: AlertSeverity;
  payload: Record<string, unknown>;
  resolved_at: string | null;
  resolved_by: { id: string; name: string } | null;
  created_at: string | null;
}

export type NotificationKind = string;

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  payload: Record<string, unknown>;
  read_at: string | null;
  created_at: string | null;
}

export interface PatientNote {
  id: string;
  body: string;
  author: { id: string; name: string };
  created_at: string | null;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
}

export interface Reminder {
  time_of_day: string | null;
  enabled: boolean;
  channel: "email";
}

export interface NotificationPrefs {
  email_new_message: boolean;
  email_new_orientation: boolean;
  email_alert: boolean;
  email_reminder: boolean;
}
